import {
  BikeStation,
  ComputedRoute,
  InfoCard,
  Route,
  useGeoveloMap,
  useLayers,
  useSource,
  useTracker,
} from '@geovelo-frontends/commons';
import {
  Checkbox,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
} from '@mui/material';
import { lightBlue } from '@mui/material/colors';
import { useTheme } from '@mui/material/styles';
import { usePDF } from '@react-pdf/renderer';
import { Chart } from 'chart.js';
import { LngLatBounds, MapOptions } from 'maplibre-gl';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import { defaultBounds } from '../../layouts/app-content/map';
import PrintedRouteLayout, { IDataToPrint } from '../../layouts/printed-route';
import Button from '../button';
import Dialog from '../dialog';
import { InfoIcon, PrintIcon } from '../icons';

const mapId = 'full-map';
const departureMapId = 'departure-map';
const arrivalMapId = 'arrival-map';
const routeSourceId = 'route';
const routeLayerId = 'route';
const wayPointsLayerId = 'way-points';

type TProps = Omit<DialogProps, 'onClose'> & {
  bikeStationsMap: { [key: number]: BikeStation };
  elevationChart: Chart | undefined;
  onClose: () => void;
  onSave: () => void;
  route: ComputedRoute | Route;
};

function PrintDialog({
  elevationChart,
  bikeStationsMap,
  route,
  onSave,
  onClose,
  ...props
}: TProps): React.JSX.Element {
  const [dataToPrint, setDataToPrint] = useState<IDataToPrint>({
    elevation: false,
    fullMap: false,
    departureMap: false,
    arrivalMap: false,
    instructions: false,
  });
  const [features] = useState(() => {
    const { sections, wayPoints } = route;
    const features: GeoJSON.Feature[] = [];

    sections.forEach(({ features: sectionFeatures, transportMode }) => {
      sectionFeatures.forEach((feature) => {
        features.push({
          ...feature,
          properties: { color: '#fff', width: 9, route: true },
        });

        features.push({
          ...feature,
          properties: {
            width: 5,
            color: transportMode === 'publicTransports' ? lightBlue[500] : '#326ac2',
            dashArray:
              transportMode === 'pedestrian' || transportMode === 'publicTransports' ? true : false,
            route: true,
          },
        });
      });
    });

    features.push(
      {
        type: 'Feature',
        geometry: wayPoints[0].point,
        properties: { color: '#2ac682', wayPoint: true, departure: true },
      },
      {
        type: 'Feature',
        geometry: wayPoints[wayPoints.length - 1].point,
        properties: { color: '#dd428d', wayPoint: true, arrival: true },
      },
    );

    return features;
  });
  const { t } = useTranslation();
  const { transitions } = useTheme();
  const {
    initialized: fullMapInitialized,
    map: fullMap,
    init: initFullMap,
    destroy: destoryFullMap,
  } = useGeoveloMap();
  const {
    initialized: departureMapInitialized,
    map: departureMap,
    init: initDepartureMap,
    destroy: destroyDepartureMap,
  } = useGeoveloMap();
  const {
    initialized: arrivalMapInitialized,
    map: arrivalMap,
    init: initArrivalMap,
    destroy: destroyArrivalMap,
  } = useGeoveloMap();
  const { addGeoJSONSource: addGeoJSONSourceToFullMap } = useSource(fullMap, routeSourceId);
  const { addGeoJSONSource: addGeoJSONSourceToDepartureMap } = useSource(
    departureMap,
    routeSourceId,
  );
  const { addGeoJSONSource: addGeoJSONSourceToArrivalMap } = useSource(arrivalMap, routeSourceId);
  const { addLineLayer: addRouteLayerToFullMap, addCircleLayer: addCircleLayerToFullMap } =
    useLayers(fullMap);
  const {
    addLineLayer: addRouteLayerToDepartureMap,
    addCircleLayer: addCircleLayerToDepartureMap,
  } = useLayers(departureMap);
  const { addLineLayer: addRouteLayerToArrivalMap, addCircleLayer: addCircleLayerToArrivalMap } =
    useLayers(arrivalMap);
  const [instance, updateInstance] = usePDF({
    document: (
      <PrintedRouteLayout
        arrivalMap={arrivalMap}
        bikeStationsMap={bikeStationsMap}
        dataToPrint={dataToPrint}
        departureMap={departureMap}
        elevationChart={elevationChart}
        fullMap={fullMap}
        route={route}
      />
    ),
  });
  const { trackEvent } = useTracker();

  useEffect(() => {
    if (props.open) {
      setTimeout(() => initMaps(), transitions.duration.enteringScreen);
    } else {
      destoryFullMap();
      destroyDepartureMap();
      destroyArrivalMap();
    }
  }, [props.open]);

  useEffect(() => {
    updateInstance(
      <PrintedRouteLayout
        arrivalMap={arrivalMap}
        bikeStationsMap={bikeStationsMap}
        dataToPrint={dataToPrint}
        departureMap={departureMap}
        elevationChart={elevationChart}
        fullMap={fullMap}
        route={route}
      />,
    );
  }, [dataToPrint]);

  useEffect(() => {
    if (!fullMap) return;

    const { bounds } = route;
    if (bounds) {
      const { north, east, south, west } = bounds;
      fullMap.fitBounds(new LngLatBounds({ lat: south, lng: west }, { lat: north, lng: east }), {
        animate: false,
        padding: 50,
      });
    }

    addGeoJSONSourceToFullMap({ type: 'FeatureCollection', features });

    addRouteLayerToFullMap(
      routeLayerId,
      routeSourceId,
      { 'line-join': 'round', 'line-cap': 'round' },
      {
        'line-color': ['get', 'color'],
        'line-width': ['get', 'width'],
      },
      { filter: ['==', 'route', true] },
    );

    addCircleLayerToFullMap(
      wayPointsLayerId,
      routeSourceId,
      {
        'circle-radius': 6,
        'circle-stroke-width': 2,
        'circle-stroke-opacity': 1,
        'circle-color': ['get', 'color'],
        'circle-stroke-color': '#fff',
      },
      { filter: ['==', 'wayPoint', true] },
    );
  }, [fullMap]);

  useEffect(() => {
    if (!departureMap) return;

    addGeoJSONSourceToDepartureMap({ type: 'FeatureCollection', features });

    addRouteLayerToDepartureMap(
      routeLayerId,
      routeSourceId,
      { 'line-join': 'round', 'line-cap': 'round' },
      {
        'line-color': ['get', 'color'],
        'line-width': ['get', 'width'],
      },
      { filter: ['==', 'route', true] },
    );

    addCircleLayerToDepartureMap(
      wayPointsLayerId,
      routeSourceId,
      {
        'circle-radius': 6,
        'circle-stroke-width': 2,
        'circle-stroke-opacity': 1,
        'circle-color': ['get', 'color'],
        'circle-stroke-color': '#fff',
      },
      { filter: ['==', 'departure', true] },
    );
  }, [departureMap]);

  useEffect(() => {
    if (!arrivalMap) return;

    addGeoJSONSourceToArrivalMap({ type: 'FeatureCollection', features });

    addRouteLayerToArrivalMap(
      routeLayerId,
      routeSourceId,
      { 'line-join': 'round', 'line-cap': 'round' },
      {
        'line-color': ['get', 'color'],
        'line-width': ['get', 'width'],
      },
    );

    addCircleLayerToArrivalMap(
      wayPointsLayerId,
      routeSourceId,
      {
        'circle-radius': 6,
        'circle-stroke-width': 2,
        'circle-stroke-opacity': 1,
        'circle-color': ['get', 'color'],
        'circle-stroke-color': '#fff',
      },
      { filter: ['==', 'arrival', true] },
    );
  }, [arrivalMap]);

  function initMaps() {
    const { wayPoints } = route;

    const options: Omit<MapOptions, 'container' | 'style'> = {
      preserveDrawingBuffer: true,
      interactive: false,
    };

    initFullMap({
      ...options,
      container: mapId,
      bounds: defaultBounds,
    });

    const departureCoordinates = wayPoints[0].point.coordinates;
    initDepartureMap({
      ...options,
      container: departureMapId,
      center: { lat: departureCoordinates[1], lng: departureCoordinates[0] },
      zoom: 18,
    });

    const arrivalCoordinates = wayPoints[wayPoints.length - 1].point.coordinates;
    initArrivalMap({
      ...options,
      container: arrivalMapId,
      center: { lat: arrivalCoordinates[1], lng: arrivalCoordinates[0] },
      zoom: 18,
    });
  }

  return (
    <Dialog fullWidth maxWidth="md" onClose={onClose} {...props}>
      <DialogTitle>{t('geovelo.routing.print_dialog.title')}</DialogTitle>
      <DialogContent>
        <StyledInfoCard
          description={t('geovelo.routing.print_dialog.description', {
            buttonLabel: t('commons.actions.save'),
            context: route instanceof Route ? 'saved' : '',
          })}
          Icon={InfoIcon}
        />
        <FormControl component="fieldset">
          <FormLabel component="legend">
            {t('geovelo.routing.print_dialog.data_to_print')}
          </FormLabel>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={dataToPrint.elevation}
                  onChange={(_, checked) => setDataToPrint({ ...dataToPrint, elevation: checked })}
                  size="small"
                />
              }
              label={t('commons.routing.detail.elevation_profile')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={dataToPrint.fullMap}
                  onChange={(_, checked) => setDataToPrint({ ...dataToPrint, fullMap: checked })}
                  size="small"
                />
              }
              label={t('geovelo.routing.print_dialog.full_map')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={dataToPrint.departureMap}
                  onChange={(_, checked) =>
                    setDataToPrint({ ...dataToPrint, departureMap: checked })
                  }
                  size="small"
                />
              }
              label={t('geovelo.routing.print_dialog.departure_map')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={dataToPrint.arrivalMap}
                  onChange={(_, checked) => setDataToPrint({ ...dataToPrint, arrivalMap: checked })}
                  size="small"
                />
              }
              label={t('geovelo.routing.print_dialog.arrival_map')}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={dataToPrint.instructions}
                  onChange={(_, checked) => {
                    setDataToPrint({ ...dataToPrint, instructions: checked });
                  }}
                  size="small"
                />
              }
              label={t('geovelo.routing.print_dialog.instructions')}
            />
          </FormGroup>
        </FormControl>
        <MapWrapper>
          <div id={mapId} />
        </MapWrapper>
        <MapWrapper>
          <div id={departureMapId} />
        </MapWrapper>
        <MapWrapper>
          <div id={arrivalMapId} />
        </MapWrapper>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose()}>{t('commons.actions.cancel')}</Button>
        {route instanceof ComputedRoute && (
          <Button color="primary" onClick={() => onSave()}>
            {t('commons.actions.save')}
          </Button>
        )}
        <Button
          color="primary"
          component="a"
          disabled={
            instance.loading ||
            Boolean(instance.error) ||
            !instance.url ||
            !fullMapInitialized ||
            !departureMapInitialized ||
            !arrivalMapInitialized
          }
          download={`${t('commons.routing.roadmap.title').replace(/ /g, '_').toLowerCase()}.pdf`}
          href={instance.url || ''}
          onClick={() => trackEvent('Roadmap', 'Printed')}
          rel="noreferrer"
          startIcon={<PrintIcon />}
          style={{ marginLeft: 8 }}
          target="_blank"
          variant="contained"
        >
          {t('commons.actions.print')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const StyledInfoCard = styled(InfoCard)`
  background-color: whitesmoke;
  margin-bottom: 24px;
`;

const MapWrapper = styled.div`
  margin-top: 16px;
  padding-top: 100%;
  position: relative;

  > div {
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }
`;

export default PrintDialog;
